import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './types';
import type { BiometricSensorInfo } from '@sbaiahmed1/react-native-biometrics';

export type SecurityRuntimeState = {
  backofficeLastUnlockAtMs: number | null;
  securityGateStatus: 'checking' | 'locked' | 'unlocked';
  biometricInFlight: boolean;
  lastBackgroundAtMs: number | null;
  currentRouteName: string | null;
  sensorInfo: {
    checked: boolean;
    info: BiometricSensorInfo | null;
  };
};

const initialState: SecurityRuntimeState = {
  backofficeLastUnlockAtMs: null,
  securityGateStatus: 'checking',
  biometricInFlight: false,
  lastBackgroundAtMs: null,
  currentRouteName: null,
  sensorInfo: {
    checked: false,
    info: null,
  },
};

const securityRuntimeSlice = createSlice({
  name: 'securityRuntime',
  initialState,
  reducers: {
    setBackofficeLastUnlockAtMs: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.backofficeLastUnlockAtMs = action.payload;
    },
    resetBackofficeUnlock: state => {
      state.backofficeLastUnlockAtMs = null;
    },
    setSecurityGateStatus: (
      state,
      action: PayloadAction<'checking' | 'locked' | 'unlocked'>,
    ) => {
      state.securityGateStatus = action.payload;
    },
    setBiometricInFlight: (state, action: PayloadAction<boolean>) => {
      state.biometricInFlight = action.payload;
    },
    setLastBackgroundAtMs: (state, action: PayloadAction<number | null>) => {
      state.lastBackgroundAtMs = action.payload;
    },
    setCurrentRouteName: (state, action: PayloadAction<string | null>) => {
      state.currentRouteName = action.payload;
    },
    setSensorInfo: (
      state,
      action: PayloadAction<BiometricSensorInfo | null>,
    ) => {
      state.sensorInfo.checked = true;
      state.sensorInfo.info = action.payload;
    },
  },
});

export const {
  setBackofficeLastUnlockAtMs,
  resetBackofficeUnlock,
  setSecurityGateStatus,
  setBiometricInFlight,
  setLastBackgroundAtMs,
  setCurrentRouteName,
  setSensorInfo,
} = securityRuntimeSlice.actions;

export const selectSecurityRuntime = (state: RootState) =>
  state.securityRuntime;
export const selectBackofficeLastUnlockAtMs = (state: RootState) =>
  state.securityRuntime.backofficeLastUnlockAtMs;
export const selectSecurityGateStatus = (state: RootState) =>
  state.securityRuntime.securityGateStatus;
export const selectBiometricInFlight = (state: RootState) =>
  state.securityRuntime.biometricInFlight;
export const selectCurrentRouteName = (state: RootState) =>
  state.securityRuntime.currentRouteName;
export const selectSensorInfo = (state: RootState) =>
  state.securityRuntime.sensorInfo;

export default securityRuntimeSlice.reducer;
